import { useEffect, useState } from "react";
import "@/App.css";
import { Phone, MapPin, Clock, Award, TrendingUp, Shield, ChevronDown } from "lucide-react";

const PHONE_NUMBER = "+91 99102 33302";
const WHATSAPP_LINK = "https://wa.me/919910233302";
const ADDRESS = "B169, Dayanand Colony, Lajpat Nagar 4, New Delhi 110024";
const MAPS_LINK = "https://maps.app.goo.gl/1Q6pMvqMNwEeb6EV8";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_meenavati-site/artifacts/ryaeetxe_Meenavati%20Logo_FInal%20pdf-images-1.jpg.jpeg";
const HERO_IMAGE_URL = "https://customer-assets.emergentagent.com/job_meenavati-site/artifacts/kygt807m__DSC8766.JPG.jpeg";

const collectionImages = [
  { src: "https://customer-assets.emergentagent.com/job_ethnic-wholesale-1/artifacts/4hjlvegi_4D3A9855.JPG.jpeg", caption: "Vibrant Elegance" },
  { src: "https://customer-assets.emergentagent.com/job_ethnic-wholesale-1/artifacts/2kfh8i5x_328A3181.JPG.jpeg", caption: "Radiant Beauty" },
  { src: "https://customer-assets.emergentagent.com/job_b013912a-e9ec-4e2d-b7d9-17c687de7f89/artifacts/bqbhbccb_IMG_9490.JPG.jpeg", caption: "Festive Splendor" },
  { src: "https://customer-assets.emergentagent.com/job_b013912a-e9ec-4e2d-b7d9-17c687de7f89/artifacts/9pxo9lgi_IMG_9493.JPG.jpeg", caption: "Heritage Luxe" },
  { src: "https://customer-assets.emergentagent.com/job_ethnic-wholesale-1/artifacts/a4djcl0b_IMG_9491.JPG.jpeg", caption: "Timeless Beauty" },
  { src: "https://customer-assets.emergentagent.com/job_ethnic-wholesale-1/artifacts/quy0d5r5_IMG_9480.JPG.jpeg", caption: "Royal Grandeur" }
];

const whyUsData = [
  { icon: Award, title: "Premium Quality", desc: "Handpicked fabrics and intricate craftsmanship in every piece" },
  { icon: TrendingUp, title: "Best Wholesale Rates", desc: "Competitive prices directly from our manufacturing unit" },
  { icon: Shield, title: "Trusted Partner", desc: "10+ years serving retailers across India with reliability" }
];

function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="App">
      <div className="landing-page">
        {/* Header */}
        <header className={`header ${scrolled ? "header-scrolled" : ""}`}>
          <div className="header-inner">
            <div className="logo-box">
              <img src={LOGO_URL} alt="Meenavati" className="logo-img" />
            </div>
            <nav className="nav-menu">
              <button onClick={() => scrollToSection("home")} className="nav-link">HOME</button>
              <button onClick={() => scrollToSection("collection")} className="nav-link">COLLECTION</button>
              <button onClick={() => scrollToSection("about")} className="nav-link">ABOUT</button>
              <button onClick={() => scrollToSection("contact")} className="nav-link">CONTACT</button>
            </nav>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="header-btn">
              BOOK APPOINTMENT
            </a>
          </div>
        </header>

        {/* Hero */}
        <section id="home" className="hero-section" style={{ backgroundImage: `url(${HERO_IMAGE_URL})` }}>
          <div className="hero-overlay"></div>
          <div className="hero-content">
            <div className="hero-badge">Since 2015</div>
            <h1 className="hero-title">The Art of Ethnic Wholesale</h1>
            <p className="hero-subtitle">
              Delhi's premier destination for women's ethnic wear. Serving retailers with
              <span className="hero-highlight"> factory-direct pricing and unmatched quality.</span>
            </p>
            <div className="hero-buttons">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary">
                Book Video Appointment
              </a>
              <a href={`tel:${PHONE_NUMBER}`} className="btn-secondary">
                <Phone size={18} /> Call Now
              </a>
            </div>
            <button className="scroll-arrow" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}>
              <ChevronDown size={32} />
            </button>
          </div>
        </section>

        {/* Why Section */}
        <section className="why-section">
          <div className="container">
            <div className="why-grid">
              {whyUsData.map((item, i) => (
                <div key={i} className="why-card">
                  <div className="why-icon"><item.icon size={36} /></div>
                  <h3 className="why-title">{item.title}</h3>
                  <p className="why-desc">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About */}
        <section id="about" className="about-section">
          <div className="container">
            <div className="about-content">
              <h2 className="section-title">A Decade of Excellence in Delhi</h2>
              <div className="divider"></div>
              <p className="about-text">
                Located in the heart of Lajpat Nagar, <strong>Meenavati Fashion</strong> is more than a wholesaler; 
                we are your manufacturing partner. From our establishment in <strong>June 2015</strong> to becoming 
                a staple for retailers across India, we prioritize fabric quality, intricate design, and reliable B2B service.
              </p>
              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-num">10+</div>
                  <div className="stat-label">Years Excellence</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-num">1000+</div>
                  <div className="stat-label">Retail Partners</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-num">100%</div>
                  <div className="stat-label">Quality Assured</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Collection */}
        <section id="collection" className="collection-section">
          <div className="container">
            <h2 className="section-title">Our Curated Collection</h2>
            <div className="divider"></div>
            <p className="collection-intro">
              Experience the finest selection of handcrafted ethnic wear, where traditional artistry meets contemporary elegance.
            </p>
            <div className="collection-grid">
              {collectionImages.map((img, i) => (
                <div key={i} className="collection-item" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="collection-img-wrap">
                    <img src={img.src} alt={img.caption} className="collection-img" loading="lazy" />
                    <div className="collection-overlay">
                      <p className="collection-caption">{img.caption}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="collection-cta">
              <p className="collection-cta-text">Interested in viewing our complete catalog?</p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary">
                Request Full Catalog
              </a>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="contact-section">
          <div className="container">
            <h2 className="section-title">Visit Our Showroom</h2>
            <div className="divider"></div>
            <div className="contact-layout">
              <div className="contact-info">
                <div className="contact-card">
                  <div className="contact-icon-box"><MapPin size={28} /></div>
                  <div>
                    <h3 className="contact-label">Our Location</h3>
                    <p className="contact-detail">{ADDRESS}</p>
                    <a href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="contact-link">Get Directions →</a>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-icon-box"><Phone size={28} /></div>
                  <div>
                    <h3 className="contact-label">Call Us</h3>
                    <p className="contact-detail">{PHONE_NUMBER}</p>
                    <a href={`tel:${PHONE_NUMBER}`} className="contact-link">Call Now →</a>
                  </div>
                </div>
                <div className="contact-card">
                  <div className="contact-icon-box"><Clock size={28} /></div>
                  <div>
                    <h3 className="contact-label">Business Hours</h3>
                    <p className="contact-detail">Mon - Sat: 10:00 AM - 7:00 PM</p>
                    <p className="contact-detail sub">Sunday: By Appointment</p>
                  </div>
                </div>
              </div>
              <div className="contact-map">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d875.1!2d77.2452365!3d28.5630809!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce30020b6ad11%3A0x7a7f7fd90a08393e!2sMeenavati%20Fashion%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1706300000000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Meenavati Fashion Pvt Ltd Location"
                ></iframe>
              </div>
            </div>
            <div className="cta-box">
              <h3 className="cta-box-title">Ready to Partner With Us?</h3>
              <p className="cta-box-text">
                Schedule a personalized video appointment to view our latest collection and discuss exclusive wholesale opportunities for your retail business.
              </p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large">
                Book Video Appointment on WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <div className="footer-logo-box">
                  <img src={LOGO_URL} alt="Meenavati Fashion" className="footer-logo" />
                </div>
                <p className="footer-tagline">Delhi's Premier Wholesale Ethnic Wear Since 2015</p>
              </div>
              <div className="footer-links">
                <h4>Quick Links</h4>
                <ul>
                  <li><button onClick={() => scrollToSection("home")}>Home</button></li>
                  <li><button onClick={() => scrollToSection("collection")}>Our Collection</button></li>
                  <li><button onClick={() => scrollToSection("about")}>About Us</button></li>
                  <li><button onClick={() => scrollToSection("contact")}>Contact</button></li>
                </ul>
              </div>
              <div className="footer-contact">
                <h4>Contact Info</h4>
                <p>{PHONE_NUMBER}</p>
                <p>Lajpat Nagar 4</p>
                <p>New Delhi 110024</p>
              </div>
              <div className="footer-hours">
                <h4>Business Hours</h4>
                <p>Mon - Sat: 10 AM - 7 PM</p>
                <p>Sunday: By Appointment</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>© 2025 Meenavati Fashion Pvt Ltd. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
